/**
 * Gastro Systems: Zusatzverkauf im Warenkorb + generisches Quick-Add.
 *
 * <gs-quick-add>   Faengt das Submit des No-JS-Formulars (POST /cart/add) ab, legt per
 *                  /cart/add.js in den Warenkorb und loest – genau wie Horizons
 *                  product-form.js – ein `CartLinesUpdateEvent` (@shopify/events) mit
 *                  Promise aus. Das Promise wird mit dem AJAX-Warenkorb und den
 *                  gerenderten `sections` aller <cart-items-component> aufgeloest, damit
 *                  Drawer, Warenkorbseite, Zaehler-Badge und Gratis-Lieferungs-Balken
 *                  ohne Seitenwechsel aktualisieren (und der Drawer ggf. automatisch oeffnet).
 *
 * <gs-cart-upsell> Laedt "Passend dazu" lazy (Drawer: beim Oeffnen, Seite: nach dem Laden)
 *                  ueber die Product Recommendations API mit Section Rendering.
 *                  complementary -> Fallback related. Schliesst Warenkorbprodukte aus,
 *                  bevorzugt "Luecken-Fueller" bis zur Gratis-Lieferung.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const MAX_ROWS = 4;
const RECO_LIMIT = 6;
/** Luecken-Fueller nur, wenn der Restbetrag hoechstens CHF 150 betraegt. */
const GAP_MAX_CENTS = 15000;
/** Bevorzugt werden Produkte mit Preis zwischen Restbetrag und Restbetrag + CHF 30. */
const GAP_TOLERANCE_CENTS = 3000;
const FEEDBACK_MS = 2000;

/** @returns {{ cartAddUrl: string, cartUrl: string }} */
function routes() {
  const r = /** @type {any} */ (window).Theme?.routes ?? {};
  return {
    cartAddUrl: r.cart_add_url || '/cart/add.js',
    cartUrl: r.cart_url || '/cart',
  };
}

/** @returns {string[]} Section-IDs aller Warenkorb-Komponenten auf der Seite. */
function cartSectionIds() {
  const ids = new Set();
  for (const el of document.querySelectorAll('cart-items-component')) {
    if (el instanceof HTMLElement && el.dataset.sectionId) ids.add(el.dataset.sectionId);
  }
  return Array.from(ids);
}

async function fetchCart() {
  const response = await fetch(`${routes().cartUrl}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Cart fetch failed: ${response.status}`);
  return response.json();
}

/**
 * Quick-Add fuer Einzelvarianten-Produkte.
 */
class GsQuickAdd extends HTMLElement {
  #busy = false;
  /** @type {number | undefined} */
  #timer;

  connectedCallback() {
    this.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.#onSubmit);
    clearTimeout(this.#timer);
  }

  /** @param {Event} event */
  #onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    event.preventDefault();
    this.add(form);
  };

  /** @param {'idle' | 'loading' | 'added' | 'error'} state */
  #setState(state) {
    const button = this.querySelector('button');
    clearTimeout(this.#timer);
    if (state === 'idle') {
      delete this.dataset.state;
    } else {
      this.dataset.state = state;
    }
    if (button) {
      button.setAttribute('aria-disabled', state === 'loading' ? 'true' : 'false');
      if (state === 'loading') button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    }
    this.#toggleLabel('idle', state === 'idle' || state === 'loading');
    this.#toggleLabel('added', state === 'added');
    this.#toggleLabel('error', state === 'error');
    if (state === 'added' || state === 'error') {
      this.#timer = window.setTimeout(() => this.#setState('idle'), FEEDBACK_MS);
    }
  }

  /**
   * @param {string} name
   * @param {boolean} visible
   */
  #toggleLabel(name, visible) {
    const label = this.querySelector(`.gs-quick-add__label--${name}`);
    if (label instanceof HTMLElement) label.hidden = !visible;
  }

  /**
   * Legt die Variante des Formulars in den Warenkorb.
   * @param {HTMLFormElement} form
   */
  async add(form) {
    if (this.#busy) return;
    const variantId = String(new FormData(form).get('id') || '');
    const quantity = Math.max(1, Number(new FormData(form).get('quantity')) || 1);
    if (!variantId) return;

    this.#busy = true;
    this.#setState('loading');

    const sectionIds = cartSectionIds();
    const deferred = CartLinesUpdateEvent.createPromise();
    const context = this.dataset.context === 'cart' ? 'cart' : 'product';

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context,
        lines: [{ merchandiseId: variantId, quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(routes().cartAddUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity }],
          ...(sectionIds.length ? { sections: sectionIds.join(',') } : {}),
        }),
      });
      const data = await response.json();

      if (!response.ok || data.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: data.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: data.description, errors: data.errors },
          })
        );
        const cart = await fetchCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            didError: true,
            items: cart.items,
            source: 'gs-quick-add',
            itemCount: quantity,
            productId: this.dataset.productId,
          },
        });
        console.warn('[gs-quick-add]', data.description || data.message);
        this.#setState('error');
        return;
      }

      const cart = await fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'gs-quick-add',
          itemCount: quantity,
          productId: this.dataset.productId,
          sections: data.sections,
        },
      });
      this.#setState('added');
    } catch (error) {
      deferred.reject(error);
      console.warn('[gs-quick-add] Hinzufuegen fehlgeschlagen:', error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: /** @type {Error} */ (error)?.message || 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#setState('error');
    } finally {
      this.#busy = false;
    }
  }
}

/**
 * "Passend dazu" im Warenkorb.
 */
class GsCartUpsell extends HTMLElement {
  static observedAttributes = ['data-product-id', 'data-exclude', 'data-gap-cents'];

  /** @type {AbortController | null} */
  #controller = null;
  /** @type {Element | null} */
  #drawer = null;
  #stale = true;
  #queued = false;
  /** @type {Map<string, string>} */
  #cache = new Map();

  connectedCallback() {
    this.#drawer = this.closest('theme-drawer');
    if (this.#drawer) {
      this.#drawer.addEventListener('theme-drawer:open', this.#onDrawerOpen);
      if (this.#drawer.hasAttribute('open')) this.#schedule();
    } else {
      this.#schedule(true);
    }
  }

  disconnectedCallback() {
    this.#drawer?.removeEventListener('theme-drawer:open', this.#onDrawerOpen);
    this.#drawer = null;
    this.#controller?.abort();
    this.#controller = null;
  }

  /**
   * Horizons Cart-Morph aktualisiert die data-Attribute -> neu laden (sofort, wenn sichtbar).
   * @param {string} _name
   * @param {string | null} oldValue
   * @param {string | null} newValue
   */
  attributeChangedCallback(_name, oldValue, newValue) {
    if (oldValue === null || oldValue === newValue || !this.isConnected) return;
    this.#stale = true;
    if (this.#isVisible()) this.#schedule();
  }

  #onDrawerOpen = () => {
    if (this.#stale) this.#schedule();
  };

  #isVisible() {
    return !this.#drawer || this.#drawer.hasAttribute('open');
  }

  /** @param {boolean} [idle] */
  #schedule(idle = false) {
    if (this.#queued) return;
    this.#queued = true;
    const run = () => {
      this.#queued = false;
      if (this.isConnected) this.#load();
    };
    if (idle && 'requestIdleCallback' in window) {
      window.requestIdleCallback(run, { timeout: 2000 });
    } else {
      setTimeout(run, 0);
    }
  }

  /** @returns {Set<string>} */
  get #excluded() {
    return new Set((this.dataset.exclude || '').split(',').filter(Boolean));
  }

  async #load() {
    this.#stale = false;
    this.#controller?.abort();
    const controller = new AbortController();
    this.#controller = controller;

    const productId = this.dataset.productId;
    if (!productId) {
      this.#render([]);
      return;
    }

    try {
      let items = await this.#fetchItems(productId, 'complementary', controller.signal);
      if (!items.length) items = await this.#fetchItems(productId, 'related', controller.signal);
      if (controller.signal.aborted) return;
      this.#render(items);
    } catch (error) {
      if (/** @type {Error} */ (error)?.name === 'AbortError') return;
      console.warn('[gs-cart-upsell] Empfehlungen nicht geladen:', error);
      this.#render([]);
    } finally {
      if (this.#controller === controller) this.#controller = null;
    }
  }

  /**
   * @param {string} productId
   * @param {'complementary' | 'related'} intent
   * @param {AbortSignal} signal
   * @returns {Promise<HTMLElement[]>}
   */
  async #fetchItems(productId, intent, signal) {
    const url = new URL(this.dataset.url || '/recommendations/products', window.location.origin);
    url.searchParams.set('product_id', productId);
    url.searchParams.set('intent', intent);
    url.searchParams.set('limit', String(RECO_LIMIT));
    url.searchParams.set('section_id', this.dataset.sectionId || 'gs-cart-recommendations');

    // Die Antwort haengt vom Warenkorb ab (serverseitiger Ausschluss) -> Cache je Warenkorbstand.
    const key = `${url}|${this.dataset.exclude || ''}`;
    let html = this.#cache.get(key);
    if (html === undefined) {
      const response = await fetch(url, { signal, credentials: 'same-origin' });
      if (!response.ok) throw new Error(`Recommendations ${intent}: HTTP ${response.status}`);
      html = await response.text();
      if (this.#cache.size > 20) this.#cache.clear();
      this.#cache.set(key, html);
    }

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const excluded = this.#excluded;
    const seen = new Set();
    /** @type {HTMLElement[]} */
    const items = [];
    for (const node of doc.querySelectorAll('.gs-cart-reco__list > .gs-mini')) {
      if (!(node instanceof HTMLElement)) continue;
      const id = node.dataset.productId || '';
      if (!id || excluded.has(id) || seen.has(id)) continue;
      seen.add(id);
      items.push(node);
    }
    return items;
  }

  /** @param {HTMLElement[]} items */
  #render(items) {
    const root = this.querySelector('[data-gs-upsell-root]');
    const list = this.querySelector('[data-gs-upsell-list]');
    if (!(root instanceof HTMLElement) || !(list instanceof HTMLElement)) return;

    const hadFocus = this.contains(document.activeElement);
    const gap = Number(this.dataset.gapCents) || 0;
    /** @type {HTMLElement[]} */
    let fillers = [];

    if (gap > 0 && gap <= GAP_MAX_CENTS) {
      fillers = items
        .filter((item) => {
          const price = Number(item.dataset.priceCents) || 0;
          return item.dataset.quickAdd === 'true' && price >= gap && price <= gap + GAP_TOLERANCE_CENTS;
        })
        .sort((a, b) => (Number(a.dataset.priceCents) || 0) - (Number(b.dataset.priceCents) || 0));
    }

    const ordered = [...fillers, ...items.filter((item) => !fillers.includes(item))].slice(0, MAX_ROWS);

    for (const item of ordered) {
      const hint = item.querySelector('[data-gs-hint]');
      if (hint instanceof HTMLElement) hint.hidden = !fillers.includes(item);
    }

    list.replaceChildren(...ordered.map((item) => document.importNode(item, true)));
    root.hidden = ordered.length === 0;

    // Fokus nicht im Nichts verlieren, wenn die aktivierte Zeile ersetzt wurde.
    if (hadFocus && !this.contains(document.activeElement)) {
      const heading = root.querySelector('.gs-upsell__heading');
      if (!root.hidden && heading instanceof HTMLElement) heading.focus({ preventScroll: true });
    }
  }
}

if (!customElements.get('gs-quick-add')) customElements.define('gs-quick-add', GsQuickAdd);
if (!customElements.get('gs-cart-upsell')) customElements.define('gs-cart-upsell', GsCartUpsell);
